import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const location = useLocation();
  const projectReference = location.state?.projectReference || null;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: projectReference
      ? `Quote request: similar to "${projectReference.title}"`
      : '',
    message: projectReference
      ? `Hi Brainwave team,\n\nI saw your ${projectReference.category} project "${projectReference.title}" and I'd love to discuss something similar for my own space.\n\n(Please share a few details about your project: location, timeline, budget range, any specific features you'd like.)\n\nThanks!`
      : '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSectionRef = useRef(null);

  // When user arrived with a project reference, scroll the form into view so the prefill is visible
  useEffect(() => {
    if (projectReference && formSectionRef.current) {
      formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [projectReference]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.', {
        duration: 4000,
        style: { backgroundColor: '#00CED1', color: 'white' }
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Get In <span style={{ color: '#00CED1' }}>Touch</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Let's discuss your project and bring your architectural vision to life
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-0 shadow-lg" ref={formSectionRef}>
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-6">Send us a Message</h2>

                {projectReference && (
                  <div
                    className="mb-6 rounded-lg p-4 border-l-4 flex items-start justify-between gap-3"
                    style={{ backgroundColor: '#00CED115', borderColor: '#00CED1' }}
                    data-testid="contact-project-reference"
                  >
                    <div className="text-sm">
                      <span className="font-semibold" style={{ color: '#00CED1' }}>
                        Enquiring about:
                      </span>{' '}
                      <span className="font-medium">{projectReference.title}</span>{' '}
                      <span className="text-gray-500 capitalize">({projectReference.category})</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="h-12"
                      style={{ borderColor: '#00CED130' }}
                      data-testid="contact-form-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="h-12"
                      style={{ borderColor: '#00CED130' }}
                      data-testid="contact-form-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+27 (011) 568 1863"
                      className="h-12"
                      style={{ borderColor: '#00CED130' }}
                      data-testid="contact-form-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-base font-semibold mb-2 block">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Project Inquiry"
                      className="h-12"
                      style={{ borderColor: '#00CED130' }}
                      data-testid="contact-form-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your project..."
                      rows={6}
                      className="resize-none"
                      style={{ borderColor: '#00CED130' }}
                      data-testid="contact-form-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full text-white font-semibold hover:scale-105 transition-transform duration-300"
                    style={{ backgroundColor: '#00CED1' }}
                    data-testid="contact-form-submit-btn"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Reach out to us for consultations, project inquiries, or any questions about our services. 
                  We're here to help transform your vision into reality.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        <MapPin className="h-6 w-6" style={{ color: '#00CED1' }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">Our Office</h3>
                        <p className="text-gray-600">
                          Unit 7, INOspace Tungsten Works<br />
                          7 CR Swart Dr, Strydompark<br />
                          Randburg, 2169<br />
                          South Africa
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        <Phone className="h-6 w-6" style={{ color: '#00CED1' }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">Phone</h3>
                        <p className="text-gray-600">+27 (011) 568 1863</p>
                        <p className="text-sm text-gray-500 mt-1">Mon-Fri, 9AM-6PM</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        <Mail className="h-6 w-6" style={{ color: '#00CED1' }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">Email</h3>
                        <p className="text-gray-600">sales@brainwave-group.com</p>
                        <p className="text-sm text-gray-500 mt-1">24/7 Support</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        <Clock className="h-6 w-6" style={{ color: '#00CED1' }} />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">Business Hours</h3>
                        <p className="text-gray-600">
                          Monday - Friday: 9:00 AM - 6:00 PM<br />
                          Saturday: Closed<br />
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0 px-0 bg-gray-100">
        <div className="w-full h-96 relative">
          <iframe
            title="BrainWave Architecture Location - Unit 7, INOspace Tungsten Works, Strydompark, Randburg"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4288.195465120815!2d27.969831576192856!3d-26.07912265889309!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e95751a7e7b1e85%3A0x2843eec35156c03a!2sBrainWave%20Architectural%20Projects!5e1!3m2!1sen!2sza!4v1773848285815!5m2!1sen!2sza"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0"
          />
          {/* Fallback: Click to open in Google Maps */}
          <div className="absolute bottom-4 right-4 z-10">
            <a
              href="https://www.google.com/maps/place/BrainWave+Architectural+Projects/@-26.0791227,27.9698316,17z/data=!3m1!4b1!4m6!3m5!1s0x1e95751a7e7b1e85:0x2843eec35156c03a!8m2!3d-26.0791227!4d27.9724065!16s%2Fg%2F11y5k_bk8q"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white px-4 py-2 rounded-lg shadow-lg text-sm font-semibold hover:shadow-xl transition-shadow duration-300"
              style={{ color: '#00CED1' }}
            >
              View in Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
